import type { AppConfig } from "../../types/appConfig";
import type {
  Prestation,
  PrestationRequest,
  Product,
  ProductRequest,
  CustomerProduct,
  CounterMovement,
  PurchaseRequest,
  BatchConsumeRequest,
  BatchConsumeResult,
  AdjustRequest,
} from "./Billing.typed";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";

export class BillingModel {
  private base: string;

  constructor(config: AppConfig) {
    this.base = config.zekayAPI;
  }

  // -- Groups ---------------------------------------------------------------

  async listGroups(entity: "prestations" | "products"): Promise<string[]> {
    const res = await fetchZekay(`${this.base}/billing/groups?entity=${entity}`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as string[];
  }

  // -- Prestations ----------------------------------------------------------

  async listPrestations(): Promise<Prestation[]> {
    const res = await fetchZekay(`${this.base}/prestations`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as Prestation[];
  }

  async getPrestation(id: number): Promise<Prestation> {
    const res = await fetchZekay(`${this.base}/prestations/${id}`);
    await assertResponseOk(res);
    return (await res.json()) as Prestation;
  }

  async createPrestation(payload: PrestationRequest): Promise<Prestation> {
    const res = await fetchZekay(`${this.base}/prestations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as Prestation;
  }

  async updatePrestation(id: number, payload: PrestationRequest): Promise<Prestation> {
    const res = await fetchZekay(`${this.base}/prestations/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    return (await res.json()) as Prestation;
  }

  async deletePrestation(id: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/prestations/${id}`, { method: "DELETE" });
    await expectStatuses(res, 204);
  }

  // -- Products --------------------------------------------------------------

  async listProducts(): Promise<Product[]> {
    const res = await fetchZekay(`${this.base}/products`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as Product[];
  }

  async getProduct(id: number): Promise<Product> {
    const res = await fetchZekay(`${this.base}/products/${id}`);
    await assertResponseOk(res);
    return (await res.json()) as Product;
  }

  async createProduct(payload: ProductRequest): Promise<Product> {
    const res = await fetchZekay(`${this.base}/products`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as Product;
  }

  async updateProduct(id: number, payload: ProductRequest): Promise<Product> {
    const res = await fetchZekay(`${this.base}/products/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    return (await res.json()) as Product;
  }

  async deleteProduct(id: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/products/${id}`, { method: "DELETE" });
    await expectStatuses(res, 204);
  }

  // -- Counters --------------------------------------------------------------

  async listCustomerCounters(customerId: number, status?: string): Promise<CustomerProduct[]> {
    const params = status ? `?status=${status}` : "";
    const res = await fetchZekay(`${this.base}/customers/${customerId}/counters${params}`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as CustomerProduct[];
  }

  async purchaseProduct(payload: PurchaseRequest): Promise<CustomerProduct> {
    const res = await fetchZekay(`${this.base}/counters`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as CustomerProduct;
  }

  async batchConsume(payload: BatchConsumeRequest): Promise<BatchConsumeResult[]> {
    const res = await fetchZekay(`${this.base}/counters/batch-consume`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    const body = (await res.json()) as { results: BatchConsumeResult[] };
    return body.results;
  }

  async adjustCounter(id: number, payload: AdjustRequest): Promise<CustomerProduct> {
    const res = await fetchZekay(`${this.base}/counters/${id}/adjust`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    return (await res.json()) as CustomerProduct;
  }

  async cancelCounter(id: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/counters/${id}/cancel`, { method: "POST" });
    await expectStatuses(res, 204);
  }

  async listCounterMovements(id: number): Promise<CounterMovement[]> {
    const res = await fetchZekay(`${this.base}/counters/${id}/movements`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as CounterMovement[];
  }
}
